import React, { useEffect, useMemo, useState } from "react";
import { Grid, NumberInput, Text, Stack, Title } from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import { IconPencil, IconTrash } from "@tabler/icons";
import { Page, FormSectionCard } from "../../layout/page";
import { useServerApi } from "../../hooks/userServerApi";
import _ from "lodash";
import PageListHeader from "../../components/pageListHeader";
import { showNotification } from "@mantine/notifications";
import { IconCircleCheck, IconExclamationMark } from "@tabler/icons";
import { useTabNavigate } from "../../hooks/useTabNavigate";
import { ENUM_FORM_DISPLAY, ENUM_FORM_LAYOUT_CONTAINER } from "../../components/pageList";
import LocalPageList from "../../components/localPageList";
import ActionCellRender from "../../components/actionCellRender";
import {
  AR_FORM_NUMBER_INPUT_PROPS,
} from "../../helpers/arFormUtils";
import {
  DEFAULT_MINIMUM_NON_WRAP_UPFRONT_FEE,
  formatEffectiveFromLabel,
  normalizeNonWrapUpfrontFeeRules,
  serializeNonWrapUpfrontFeeRules,
} from "../../helpers/investmentTransactionSettingsUtils";
import { normalizeCommissionRateSettings } from "../../helpers/investmentTransactionCommissionUtils";

const buildRuleFormSchema = () => ({
  title: "Non-Wrap upfront fee rule",
  display: { mode: ENUM_FORM_DISPLAY.MODAL, size: "md" },
  initialValues: {
    effectiveFrom: new Date(),
    minimumUpfrontFee: DEFAULT_MINIMUM_NON_WRAP_UPFRONT_FEE,
  },
  validate: {
    effectiveFrom: (value) => (value ? null : "Effective date is required"),
    minimumUpfrontFee: (value) =>
      value === "" || value == null ? "Minimum Non-Wrap upfront fee is required" : null,
  },
  layout: {
    containers: [
      {
        key: "rule-section",
        parent: null,
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          withBorder: false,
          withPaper: false,
          minHeight: 0,
        },
      },
    ],
    fields: [
      {
        name: "effectiveFrom",
        component: DatePicker,
        type: "datePicker",
        parent: "rule-section",
        props: {
          label: "Effective date",
          required: true,
          allowFreeInput: true,
        },
      },
      {
        name: "minimumUpfrontFee",
        component: NumberInput,
        parent: "rule-section",
        props: {
          label: "Minimum Non-Wrap upfront fee",
          required: true,
          precision: 2,
          min: 0,
          ...AR_FORM_NUMBER_INPUT_PROPS,
        },
      },
    ],
  },
});

const buildRuleTableSchema = () => ({
  columns: [
    {
      field: "effectiveFrom",
      headerName: "Effective date",
      cellRender: (_col, data) => formatEffectiveFromLabel(data.effectiveFrom),
    },
    {
      field: "minimumUpfrontFee",
      headerName: "Minimum Non-Wrap upfront fee",
      cellRender: (_col, data) =>
        data.minimumUpfrontFee != null ? `${data.minimumUpfrontFee}%` : "—",
    },
    {
      field: "action",
      headerName: "Action",
      isAction: true,
      minWidth: 90,
      cellRender: ActionCellRender,
      actions: [
        { name: "edit", label: "Edit", icon: IconPencil, props: {} },
        { name: "delete", label: "Delete", icon: IconTrash, props: {} },
      ],
    },
  ],
});

const InvestmentTransactionSetting = () => {
  const [api] = useServerApi();
  const tabNav = useTabNavigate();
  const [settingId, setSettingId] = useState(null);
  const [rows, setRows] = useState([]);
  const [rates, setRates] = useState(() => normalizeCommissionRateSettings());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const tableSchema = useMemo(() => buildRuleTableSchema(), []);
  const formSchema = useMemo(() => buildRuleFormSchema(), []);

  const fetchSetting = async () => {
    try {
      setLoading(true);
      const result = await api.search({
        apiEntity: "setting",
        pageSize: 1,
      });

      if (!result || _.isEmpty(result.docs)) {
        setSettingId(null);
        setRates(normalizeCommissionRateSettings());
        setRows(
          normalizeNonWrapUpfrontFeeRules({
            nonWrapUpfrontFeeRules: [
              {
                effectiveFrom: new Date(),
                minimumUpfrontFee: DEFAULT_MINIMUM_NON_WRAP_UPFRONT_FEE,
              },
            ],
          })
        );
        return;
      }

      const setting = result.docs[0];
      setSettingId(setting._id);
      setRows(normalizeNonWrapUpfrontFeeRules(setting.investmentTransaction));
      setRates(normalizeCommissionRateSettings(setting.investmentTransaction));
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSetting();
  }, []);

  const handleSave = async () => {
    if (!rows.length) {
      showNotification({
        title: "Save",
        color: "red",
        icon: <IconExclamationMark size={18} />,
        message: "Add at least one effective date rule.",
      });
      return;
    }

    try {
      setSaving(true);
      let result;
      const rateSettings = normalizeCommissionRateSettings(rates);
      const nonWrapUpfrontFeeRules = serializeNonWrapUpfrontFeeRules(rows);

      if (settingId) {
        const existing = await api.getById({
          apiEntity: "setting",
          id: settingId,
        });
        result = await api.updateById({
          apiEntity: "setting",
          id: settingId,
          values: {
            ...existing,
            investmentTransaction: {
              ...(existing?.investmentTransaction ?? {}),
              nonWrapUpfrontFeeRules,
              ...rateSettings,
            },
          },
        });
      } else {
        result = await api.add({
          apiEntity: "setting",
          values: {
            investmentTransaction: {
              nonWrapUpfrontFeeRules,
              ...rateSettings,
            },
          },
        });
        if (result?.success && result?.data?._id) {
          setSettingId(result.data._id);
        }
      }

      if (result?.success) {
        showNotification({
          title: "Save",
          color: "green",
          icon: <IconCircleCheck size={18} />,
          message: "Investment transaction settings saved.",
        });
        return;
      }

      showNotification({
        title: "Save",
        color: "red",
        icon: <IconExclamationMark size={18} />,
        message: api.getErrorMsg(result?.error) || "Failed to save settings.",
      });
    } catch (error) {
      console.log(error);
      showNotification({
        title: "Save",
        color: "red",
        icon: <IconExclamationMark size={18} />,
        message: "Failed to save settings.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Page>
      <PageListHeader
        title="Investment Transaction"
        actionButtons={[
          {
            label: "Save",
            onClick: handleSave,
            loading: saving,
            disabled: loading,
          },
          {
            label: "Close",
            onClick: () => tabNav.closeActive(),
            variant: "default",
          },
        ]}
      />

      {!loading && (
        <Stack spacing="md">
          <FormSectionCard>
            <Title order={5}>iFast charge rates</Title>
            <Text size="xs" color="dimmed" mb="md">
              Commission rate is the upfront fee / switch fee minus the iFast
              charge. Wrap iFast charge is that fee multiplied by the channel
              rate. Non-Wrap iFast charge is the max subscription fee multiplied
              by the channel rate.
            </Text>
            <Grid gutter="md">
              <Grid.Col span={12} md={4}>
                <NumberInput
                  label="Non-wrap max subscription fee %"
                  value={rates.nonWrapMaxSubscriptionFee}
                  onChange={(value) =>
                    setRates((current) => ({
                      ...current,
                      nonWrapMaxSubscriptionFee: value,
                    }))
                  }
                  precision={2}
                  min={0}
                  {...AR_FORM_NUMBER_INPUT_PROPS}
                />
              </Grid.Col>
              <Grid.Col span={12} md={4}>
                <NumberInput
                  label="Online iFast charge %"
                  value={rates.onlineIfastCharge}
                  onChange={(value) =>
                    setRates((current) => ({
                      ...current,
                      onlineIfastCharge: value,
                    }))
                  }
                  precision={2}
                  min={0}
                  {...AR_FORM_NUMBER_INPUT_PROPS}
                />
              </Grid.Col>
              <Grid.Col span={12} md={4}>
                <NumberInput
                  label="Paper iFast charge %"
                  value={rates.paperIfastCharge}
                  onChange={(value) =>
                    setRates((current) => ({
                      ...current,
                      paperIfastCharge: value,
                    }))
                  }
                  precision={2}
                  min={0}
                  {...AR_FORM_NUMBER_INPUT_PROPS}
                />
              </Grid.Col>
            </Grid>
          </FormSectionCard>
          <FormSectionCard>
            <Title order={5}>Non-Wrap upfront fee</Title>
            <Text size="xs" color="dimmed" mb="md">
              These effective-date rules are stored for reference only. On the
              AR Form (Part C), Non-Wrap minimum upfront fee / switch fee %
              follows submission via: Online and Paper floors are derived from
              the iFast charge rates above.
            </Text>
            <LocalPageList
              rows={rows}
              onFormSubmit={setRows}
              tableSchema={tableSchema}
              formSchema={formSchema}
              btnLabel="Add rule"
            />
          </FormSectionCard>
        </Stack>
      )}
    </Page>
  );
};

export default InvestmentTransactionSetting;
